import { isAxiosError } from "axios";

import { canUseAgent } from "@/auth/capabilities";
import queryClient from "@/queryClient";
import { queryKeys } from "@/queryKeys";
import api from "@/services/api";
import type { User } from "@/types/auth";

import { AGENT_CONTEXTS } from "./apps";
import { createInvalidation } from "./contexts/invalidation";
import type { WireMessage } from "./protocol/protocol";
import { sandboxCandidate } from "./sandboxPackages";
import { createAgentClient, TicketRefused } from "./socket/client";

const DEFAULT_WS_URL = "ws://localhost:8001/ws/agent/";
const DEVICE_KEY = "steddy:agent-device";
const HIDDEN_TAB_FLUSH_MS = 100;

/** A relative VITE_AGENT_WS_URL (a future same-origin proxy) resolves against this page. */
export function agentSocketUrl(raw = import.meta.env.VITE_AGENT_WS_URL || DEFAULT_WS_URL): string {
  const url = new URL(raw, window.location.href);
  if (url.protocol === "http:") url.protocol = "ws:";
  if (url.protocol === "https:") url.protocol = "wss:";
  return url.toString();
}

// Per page load, never sessionStorage: "Duplicate tab" copies it and two tabs would share an id.
export const TAB_ID = crypto.randomUUID();

function deviceId(): string {
  try {
    const known = localStorage.getItem(DEVICE_KEY);
    if (known) return known;
    const minted = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, minted);
    return minted;
  } catch {
    return TAB_ID; // storage blocked: this tab is its own device
  }
}

function scheduleFlush(flush: () => void) {
  let done = false;
  const once = () => {
    if (done) return;
    done = true;
    flush();
  };
  requestAnimationFrame(once);
  window.setTimeout(once, HIDDEN_TAB_FLUSH_MS);
}

export const agentClient = createAgentClient({
  session: (state) => {
    const user = (state as { auth: { user: User | null } }).auth.user;
    return user && canUseAgent(user) ? String(user.id) : null;
  },
  url: () => agentSocketUrl(),
  openSocket: (url) => new WebSocket(url),
  async mintTicket(tabId) {
    try {
      const { data } = await api.post<{ ticket: string }>("/agent/ws-ticket/", { tab_id: tabId });
      return data.ticket;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) throw new TicketRefused();
      throw error;
    }
  },
  async fetchEarlier(threadId, beforeSeq) {
    const { data } = await api.get<{ messages: WireMessage[]; has_earlier: boolean }>(
      `/agent/threads/${threadId}/messages/`,
      { params: { before_seq: beforeSeq } }
    );
    return data;
  },
  invalidation: createInvalidation(queryClient, AGENT_CONTEXTS, queryKeys.agentThreads()),
  timers: {
    now: () => Date.now(),
    setTimeout: (callback, ms) => window.setTimeout(callback, ms),
    clearTimeout: (handle) => window.clearTimeout(handle as number | undefined),
  },
  scheduleFlush,
  randomId: () => crypto.randomUUID(),
  random: Math.random,
  tabId: () => TAB_ID,
  deviceId,
  isHidden: () => document.visibilityState === "hidden",
  isOffline: () => !navigator.onLine,
  deviceKind: () => (window.matchMedia?.("(pointer: coarse)").matches ? "mobile" : "desktop"),
  onWake(callback) {
    const visible = () => document.visibilityState === "visible" && callback("visible");
    const online = () => callback("online");
    const offline = () => callback("offline");
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  },
  candidate: sandboxCandidate(),
});
